import * as React from "react";
import * as ReactDOM from "react-dom";
import { Alert, Card, Divider } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { FormItemDescription } from "../../components/antd/form/FormItemDescription";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { LinkOutlined } from "@ant-design/icons";
import { getLocalToken } from "../../services/cpln";
import { DOCS_URL, METRICS_TOKEN_UPDATE_INTERVAL_MS } from "../../envVariables";
import { CardCollapsibleContent } from "../../components/antd/card/CardCollapsibleContent";
import { getMetricsUrl } from "../../services/utils";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { defaultValues } from "../../services/defaults";
import { useCpFormValue } from "../gvc/testNewForm/utils";
import { CpSwitch } from "../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpSelect } from "../gvc/testNewForm/CpSelect";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { CpInputNumber } from "../gvc/testNewForm/CpInputNumber";
import { CpLabelTooltip } from "../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../gvc/testNewForm/CpTooltip";

interface Props {}
export const ThreatDetection: React.FC<Props> = ({}) => {
  const org = useAppSelector(selectOrg);
  const grafanaEndpoint = useAppSelector((state) => state.discovery.endpoints.grafana);

  const metricsFormRef = React.useRef<HTMLFormElement>(null as any);
  const grafanaLogsFormRef = React.useRef<HTMLFormElement>(null as any);
  const [metricsTokenFromLocal, setMetricsTokenFromLocal] = React.useState("");
  React.useEffect(() => {
    getLocalToken().then((res) => {
      if (res.accessToken !== "pass") {
        setMetricsTokenFromLocal(res.accessToken);
      }
    });
    const localTokenInterval = setInterval(() => {
      getLocalToken().then((res) => {
        if (res.accessToken !== "pass") {
          setMetricsTokenFromLocal(res.accessToken);
        }
      });
    }, METRICS_TOKEN_UPDATE_INTERVAL_MS);
    return () => {
      clearInterval(localTokenInterval);
    };
  }, []);

  const grafanaLogsRedirectUrl = `/explore?left=${encodeURIComponent(
    JSON.stringify({
      queries: [{ expr: '{container="_alerts", source="syscall"}', datasource: "logs", refId: "A" }],
      range: { from: "now-1h", to: "now" },
    })
  )}`;

  function openMetricsThreatPanel(e: any) {
    e.preventDefault();
    if (metricsFormRef.current) {
      metricsFormRef.current.submit();
    }
  }

  function openGrafanaLogs(e: any) {
    e.preventDefault();
    if (grafanaLogsFormRef.current) {
      grafanaLogsFormRef.current.submit();
    }
  }

  const isEnabled = useCpFormValue(`security.threatDetection.isEnabled`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <div>Threat Detection</div>
              <Alert
                className="ml-4"
                style={{ maxWidth: 300 }}
                showIcon
                type={`success`}
                message="Always Enabled for All Workloads"
              />
            </div>
          </CardTitle>

          <CardContent>
            <div className="mb-12">
              <FormLabel>Always-on Falco-based threat detection</FormLabel>
              <FormItemDescription>
                Control Plane provides always-on Falco-based threat detection and notification system. Enabling the
                forwarding capability cause event shipping of security-related events to a configured endpoint.
              </FormItemDescription>
              <ExternalLink label="See docs for more" url={`${DOCS_URL}/reference/org#threat-detection`} disableCopy />
            </div>
            <div className="mb-12">
              <FormLabel>Grafana Dashboard for Threats</FormLabel>
              <FormItemDescription>
                We already configured a panel in your grafana metrics dashboard so you can easily explore this
                information
              </FormItemDescription>
              <div className="mt-1">
                <a className={"cursor-pointer color-link"} onClick={openMetricsThreatPanel}>
                  <span className="mr-1">See Threats Panel in Grafana Dashboard</span>
                  <LinkOutlined />
                </a>
              </div>
            </div>
            <div className="mb-12">
              <FormLabel>Grafana Logs for Falco Alerts</FormLabel>
              <FormItemDescription>
                Explore Falco alert logs directly in Grafana
              </FormItemDescription>
              <div className="mt-1">
                <a className={"cursor-pointer color-link"} onClick={openGrafanaLogs}>
                  <span className="mr-1">Explore Falco Alerts in Grafana Logs</span>
                  <LinkOutlined />
                </a>
              </div>
            </div>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabelTooltip>
                  <CpLabel name={`security.threatDetection.isEnabled`}>Forward Detected Threats</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title:
                        "When checked, detected threats within the org cause events to be sent to a Syslog endpoint for analysis and alerting.",
                    }}
                  />
                </CpLabelTooltip>
                <CpSwitch name={`security.threatDetection.isEnabled`} />
              </div>
              <CpErrorWarning name={`security.threatDetection.isEnabled`} />
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`security.threatDetection.minimumSeverity`}>Minimum Severity</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title:
                        "Only events of the specified severity level or higher will be sent to the Syslog endpoint.",
                    }}
                  />
                </CpLabelTooltip>
                <CpSelect
                  name={`security.threatDetection.minimumSeverity`}
                  options={defaultValues.org.threatDetection.minimumSeverity.selectOptions}
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`security.threatDetection.minimumSeverity`} />
              </CpFormItem>
              <Divider orientation="left" orientationMargin={0}>
                Syslog Endpoint
              </Divider>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`security.threatDetection.syslog.transport`}>Transport</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title:
                        "The transport-layer protocol to send the syslog messages over. If TCP is chosen, messages will be sent with TLS",
                    }}
                  />
                </CpLabelTooltip>
                <CpSelect
                  name={`security.threatDetection.syslog.transport`}
                  options={defaultValues.org.threatDetection.syslog.transport.selectOptions}
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`security.threatDetection.syslog.transport`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`security.threatDetection.syslog.host`}>Host</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: "The hostname to send syslog messages to.",
                    }}
                  />
                </CpLabelTooltip>
                <CpInput name={`security.threatDetection.syslog.host`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`security.threatDetection.syslog.host`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabelTooltip>
                  <CpLabel name={`security.threatDetection.syslog.port`}>Port</CpLabel>
                  <CpTooltip
                    tooltipProps={{
                      title: "Configure detected threats to be forwarded using a specified protocol",
                    }}
                  />
                </CpLabelTooltip>
                <CpInputNumber name={`security.threatDetection.syslog.port`} />
                <CpErrorWarning name={`security.threatDetection.syslog.port`} />
              </CpFormItem>
            </CardCollapsibleContent>
          </CardContent>
          <MetricsForm>
            <form
              className="hidden"
              ref={metricsFormRef}
              action={`${grafanaEndpoint.replace("{org}", org)}/auth/process`}
              method="POST"
              target="_blank"
              rel="noopener"
            >
              <input name="redirect" value={getMetricsUrl("org") + `?viewPanel=panel-47`} type="hidden" />
              <input name="idToken" value={metricsTokenFromLocal} type="hidden" />
              <button type="submit">metrics</button>
            </form>
            <form
              className="hidden"
              ref={grafanaLogsFormRef}
              action={`${grafanaEndpoint.replace("{org}", org)}/auth/process`}
              method="POST"
              target="_blank"
              rel="noopener"
            >
              <input name="redirect" value={grafanaLogsRedirectUrl} type="hidden" />
              <input name="idToken" value={metricsTokenFromLocal} type="hidden" />
              <input name="genericToken" value={metricsTokenFromLocal} type="hidden" />
            </form>
          </MetricsForm>
        </Card>
      </div>
    </>
  );
};

function MetricsForm({ children }: React.PropsWithChildren) {
  return ReactDOM.createPortal(children, document.getElementById(`form-root`)!);
}
